import React from "react";

/**
 * Configuration object for complex localization scenarios.
 */
export interface LocalContentObject {
  /**
   * The localization key to look up in translation files.
   * If omitted, the component's children will be used as the key.
   */
  key?: string;

  /**
   * Context data passed to the localization function for dynamic content.
   * Used for variable substitution, pluralization, or conditional translations.
   */
  context?: any;

  /**
   * Additional options passed to the underlying localization system.
   */
  opts?: any;
}

/**
 * Localization prop that enables automatic translation of component content.
 */
export type LocalProp =
  /** When true, uses the component's children as the localization key */
  | boolean
  /** Explicit localization key to look up in translation files (children ignored) */
  | string
  /** Complex localization configuration. Children ignored only if 'key' is provided */
  | LocalContentObject;

/**
 * Represents hierarchical panel levels for graduated color progression.
 * Creates a visual gradient from white/black through gray until inversion.
 */
export enum PanelColor {
  /**
   * Level 0 - pure white/black background.
   */
  level0 = "level0",

  /**
   * Level 1 - very light gray.
   */
  level1 = "level1",

  /**
   * Level 2 - light gray.
   */
  level2 = "level2",

  /**
   * Level 3 - medium-light gray.
   */
  level3 = "level3",

  /**
   * Level 4 - medium gray.
   */
  level4 = "level4",

  /**
   * Level 5 - medium-dark gray.
   */
  level5 = "level5",

  /**
   * Level 6 - dark gray.
   */
  level6 = "level6",

  /**
   * Level 7 - darker gray.
   */
  level7 = "level7",

  /**
   * Level 8 - very dark gray.
   */
  level8 = "level8",

  /**
   * Level 9 - darkest gray.
   */
  level9 = "level9",

  /**
   * Level 10 - near black/white (approaching inversion).
   */
  level10 = "level10",

  /**
   * Level 11 - pure black/white (complete inversion).
   */
  level11 = "level11",
}

/**
 * Represents the standard set of theme color options used across the design system.
 * These values are typically applied to components for consistent visual styling,
 * such as background color, border, or text color based on semantic meaning.
 */
export enum ThemeColor {
  /**
   * No color styling applied.
   */
  none = "none",

  /**
   * Fully transparent color — no visual background or border.
   */
  transparent = "transparent",

  /**
   * Neutral gray, often used for muted or inactive states.
   */
  gray = "gray",

  /**
   * Primary brand color, used to highlight key actions or elements.
   */
  primary = "primary",

  /**
   * Secondary brand color, used for supporting elements or alternative emphasis.
   */
  secondary = "secondary",

  /**
   * Used to indicate an error or invalid state.
   */
  error = "error",

  /**
   * Used to signal a warning or potentially risky action.
   */
  warning = "warning",

  /**
   * Indicates a successful or completed state.
   */
  success = "success",

  /**
   * Synonym for "error" — can be used where "danger" is semantically clearer.
   */
  danger = "danger",

  /**
   * Synonym for "warning" —  can be used where "alert" is semantically clearer.
   */
  alert = "alert",

  /**
   * Synonym for "success" — typically used for completed or finished states.
   */
  done = "done",
}

export interface StyledComponentProps {
  /**
   * Additional CSS class names to apply (usually to the root of the container).
   */
  className?: string;
}

export interface StyledContainerProps extends StyledComponentProps {
  /**
   * Main content rendered prominently inside the componenet.
   */
  children?: React.ReactNode;
}

export interface BaseElementProps {
  /**
   * Additional CSS class names to apply (usually to the root of the componenet).
   */
  className?: string;

  /**
   * Inline element style object to apply (usually to the root of the componenet).
   */
  style?: React.CSSProperties;

  /**
   * Disables component interaction.
   * Note: Only applies to interactive components (Button, TextInput, etc.).
   * Display-only components either ingore this prop or apply styles to display disabled state.
   * @default false
   */
  disabled?: boolean;

  /**
   * Renders the component in a muted/de-emphasized way.
   * Use for actions and information for edge cases or minor importance to the user.
   * @default false
   */
  muted?: boolean;

  /**
   * Applies denser padding and smaller font size for compact use cases.
   * @default false
   */
  dense?: boolean;

  /**
   * Component is in a loading state that renders without data using Skeleton placeholders.
   * @default false
   */
  loading?: boolean;
}

export interface BusyStateProps {
  /**
   * Shows the componenet is performing an action and is in a busy state.
   * Usually displayed with a spinner, often used together with 'disabled' prop.
   * @default false
   */
  busy?: boolean;
}

export interface RenderPropProps {
  /**
   * Responsive CSS class names for sizing the element to be rendered.
   * Typically includes width, height, and responsive breakpoint variants.
   * @example "w-4 h-4 sm:w-5 sm:h-5"
   */
  sizeClassName: string;

  /**
   * Theme color applied by the parent component.
   * Will be a value from the imported `ThemeColor` enum.
   */
  color: ThemeColor | PanelColor;

  /**
   * Indicates the parent component is using compact styling with reduced spacing and smaller sizes.
   * Usually this will be adequatly represented in the 'sizeClassName' prop.
   */
  dense: boolean;

  /**
   * Indicates the parent component is using muted styling with reduced opacity or contrast.
   */
  muted: boolean;

  /**
   * Indicates the parent component is in a disabled state.
   */
  disabled: boolean;

  /**
   * Indicates the parent component is in a loading state.
   */
  loading: boolean;
}

/**
 * Function type for render prop patterns.
 * Provides context about the parent component's visual state and styling preferences.
 */
export type RenderProp = (props: RenderPropProps) => React.ReactNode;
